import { create } from 'zustand';

export interface WizardProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  vatRate: number;
  requiresCoverage: boolean;
  configSchema: Record<string, unknown> | null;
}

export interface WizardLocation {
  lat: number;
  lng: number;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  coverageStatus?: 'Available' | 'Unavailable' | 'Pending';
}

export interface WizardItem {
  productId: string;
  product: WizardProduct;
  locationId?: string;
  location?: WizardLocation;
  configData?: Record<string, unknown>;
  quantity?: number;
  contractMonths?: number;
}

export interface WizardDocuments {
  customerCR: File | null;
  customerVAT: File | null;
  nationalAddress: File | null;
  authorizedPersonLetter: File | null;
}

export interface WizardState {
  offerId: string | null;
  offerNumber: string | null;
  step: number;
  selectedProduct: WizardProduct | null;
  currentItem: WizardItem | null;
  items: WizardItem[];
  documents: WizardDocuments;

  setOfferId: (id: string, number: string) => void;
  setOfferNumber: (number: string) => void;
  setStep: (step: number) => void;
  setSelectedProduct: (product: WizardProduct) => void;
  setCurrentItemLocation: (location: WizardLocation, locationId: string) => void;
  setCurrentItemConfig: (config: Record<string, unknown>) => void;
  setCurrentItemQuantity: (qty: number) => void;
  setCurrentItemContractMonths: (months: number) => void;
  addItem: (item: WizardItem) => void;
  removeItem: (productId: string) => void;
  setDocument: (key: keyof WizardDocuments, file: File | null) => void;
  reset: () => void;
}

const emptyDocuments: WizardDocuments = {
  customerCR: null,
  customerVAT: null,
  nationalAddress: null,
  authorizedPersonLetter: null,
};

const initialState = {
  offerId: null,
  offerNumber: null,
  step: 0,
  selectedProduct: null,
  currentItem: null,
  items: [] as WizardItem[],
  documents: emptyDocuments,
};

export const useWizardStore = create<WizardState>((set) => ({
  ...initialState,

  setOfferId: (id, number) => set({ offerId: id, offerNumber: number }),
  setOfferNumber: (number) => set({ offerNumber: number }),
  setStep: (step) => set({ step }),
  setSelectedProduct: (product) =>
    set({ selectedProduct: product, currentItem: { productId: product.id, product } }),
  setCurrentItemLocation: (location, locationId) =>
    set((s) => ({
      currentItem: s.currentItem ? { ...s.currentItem, location, locationId } : null,
    })),
  setCurrentItemConfig: (config) =>
    set((s) => ({
      currentItem: s.currentItem ? { ...s.currentItem, configData: config } : null,
    })),
  setCurrentItemQuantity: (qty) =>
    set((s) => ({
      currentItem: s.currentItem ? { ...s.currentItem, quantity: qty } : null,
    })),
  setCurrentItemContractMonths: (months) =>
    set((s) => ({
      currentItem: s.currentItem ? { ...s.currentItem, contractMonths: months } : null,
    })),
  addItem: (item) => set((s) => ({ items: [...s.items, item], currentItem: null })),
  removeItem: (productId) =>
    set((s) => ({ items: s.items.filter((i) => i.productId !== productId) })),
  setDocument: (key, file) =>
    set((s) => ({ documents: { ...s.documents, [key]: file } })),
  reset: () => set(initialState),
}));
